import { useCallback, useMemo, useRef } from 'react';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Hook for making authenticated API calls with Clerk token
 * Handles content-type headers for API Platform (JSON-LD)
 */
export function useApi() {
  const { getToken } = useClerkAuth();

  // Store getToken in a ref to avoid dependency changes
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const request = useCallback(async (method, endpoint, data = null, options = {}) => {
    const token = await getTokenRef.current();

    // Determine content type based on method
    const isPatch = method.toLowerCase() === 'patch';
    const contentType = isPatch
      ? 'application/merge-patch+json'
      : 'application/ld+json';

    const config = {
      method,
      url: `${API_BASE_URL}${endpoint}`,
      headers: {
        'Accept': 'application/ld+json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
      ...options,
    };

    // Add content-type and data for POST/PATCH
    if (data && ['post', 'patch'].includes(method.toLowerCase())) {
      // Let axios set Content-Type automatically for FormData (includes boundary)
      if (!(data instanceof FormData)) {
        config.headers['Content-Type'] = contentType;
      }
      config.data = data;
    }

    const response = await axios(config);
    return response.data;
  }, []);

  const get = useCallback((endpoint, options) =>
    request('GET', endpoint, null, options), [request]);

  const post = useCallback((endpoint, data, options) =>
    request('POST', endpoint, data, options), [request]);

  const patch = useCallback((endpoint, data, options) =>
    request('PATCH', endpoint, data, options), [request]);

  const del = useCallback((endpoint, options) =>
    request('DELETE', endpoint, null, options), [request]);

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(() => ({ get, post, patch, del, request }), [get, post, patch, del, request]);
}

/**
 * Extract items from API Platform collection response
 * Checks both 'member' and 'hydra:member'
 */
export function extractMembers(response) {
  return response?.member || response?.['hydra:member'] || [];
}

/**
 * Extract total items from API Platform collection response
 */
export function extractTotalItems(response) {
  return response?.totalItems || response?.['hydra:totalItems'] || 0;
}
